import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Upload, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/site";

const inputCls =
  "w-full bg-transparent border-0 border-b border-dc-borderHi py-2.5 text-white placeholder:text-dc-muted focus:border-dc-accent focus:outline-none transition-colors";
const labelCls = "block text-xs uppercase tracking-wider text-dc-text2 mb-2";

const Field = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const materials = ["Cardboard / OCC", "Paper", "Packaging", "Production offcuts", "Mixed recyclable materials", "Other"];
const tonnages = ["Under 10", "10–25", "25–50", "50–100", "100–250", "250–500", "500+"];
const presentations = ["Loose", "Compacted", "Baled", "Mill-size bales", "Combination"];
const arrangements = ["Paying for collection", "Receiving rebate", "Combination", "Unsure"];

export const WasteAssessmentForm = () => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    company_name: "", site_postcode: "", contact_name: "", business_email: "", telephone: "",
    industry: "", material_type: "Cardboard / OCC", tonnes_per_month: "100–250",
    presentation: "Baled", collection_frequency: "", current_arrangement: "Paying for collection",
    contamination_level: "", artic_access: "Yes", contract_expiry: "", message: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const step1Valid = form.company_name && form.site_postcode && form.contact_name && form.business_email && form.telephone && form.industry;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/waste-assessment", form);
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/waste-assessment/${data.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setDone(true);
      toast.success("Assessment request received", { description: "Our commercial team will be in touch." });
    } catch (e) {
      toast.error("Something went wrong", { description: "Please check your details and try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="assessment-success" className="border border-dc-border bg-dc-surface p-10 text-center">
        <div className="w-14 h-14 mx-auto bg-dc-accent flex items-center justify-center mb-6"><Check className="text-dc-bg" size={28} /></div>
        <h3 className="font-display text-2xl mb-3">Request Received</h3>
        <p className="text-dc-text2 max-w-md mx-auto">
          Thank you. Our commercial team will review your waste stream and respond with a no-obligation assessment.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="waste-assessment-form" className="border border-dc-border bg-dc-surface p-6 lg:p-10">
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`h-1 flex-1 ${step >= s ? "bg-dc-accent" : "bg-dc-border"}`} />
        ))}
      </div>
      <div className="dc-label mb-1">Step {step} of 3</div>

      {step === 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
          <Field label="Company name"><input data-testid="wa-company" className={inputCls} value={form.company_name} onChange={set("company_name")} placeholder="Company Ltd" /></Field>
          <Field label="Site postcode"><input data-testid="wa-postcode" className={inputCls} value={form.site_postcode} onChange={set("site_postcode")} placeholder="e.g. SL1 4AX" /></Field>
          <Field label="Contact name"><input data-testid="wa-contact" className={inputCls} value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" /></Field>
          <Field label="Business email"><input data-testid="wa-email" type="email" className={inputCls} value={form.business_email} onChange={set("business_email")} placeholder="name@company.com" /></Field>
          <Field label="Telephone"><input data-testid="wa-phone" className={inputCls} value={form.telephone} onChange={set("telephone")} placeholder="Direct line" /></Field>
          <Field label="Industry"><input data-testid="wa-industry" className={inputCls} value={form.industry} onChange={set("industry")} placeholder="e.g. 3PL / Distribution" /></Field>
        </div>
      )}

      {step === 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
          <Field label="Material type"><select data-testid="wa-material" className={inputCls} value={form.material_type} onChange={set("material_type")}>{materials.map((m) => <option key={m} className="bg-dc-surface">{m}</option>)}</select></Field>
          <Field label="Approx. tonnes per month"><select data-testid="wa-tonnage" className={inputCls} value={form.tonnes_per_month} onChange={set("tonnes_per_month")}>{tonnages.map((m) => <option key={m} className="bg-dc-surface">{m}</option>)}</select></Field>
          <Field label="Material presentation"><select data-testid="wa-presentation" className={inputCls} value={form.presentation} onChange={set("presentation")}>{presentations.map((m) => <option key={m} className="bg-dc-surface">{m}</option>)}</select></Field>
          <Field label="Current collection frequency"><input data-testid="wa-frequency" className={inputCls} value={form.collection_frequency} onChange={set("collection_frequency")} placeholder="e.g. 3x weekly" /></Field>
          <Field label="Current arrangement"><select data-testid="wa-arrangement" className={inputCls} value={form.current_arrangement} onChange={set("current_arrangement")}>{arrangements.map((m) => <option key={m} className="bg-dc-surface">{m}</option>)}</select></Field>
          <Field label="Approx. contamination level"><input data-testid="wa-contamination" className={inputCls} value={form.contamination_level} onChange={set("contamination_level")} placeholder="e.g. Low / <5%" /></Field>
          <Field label="Articulated vehicle access?"><select data-testid="wa-artic" className={inputCls} value={form.artic_access} onChange={set("artic_access")}><option className="bg-dc-surface">Yes</option><option className="bg-dc-surface">No</option><option className="bg-dc-surface">Restricted</option></select></Field>
          <Field label="Contract renewal / expiry date"><input data-testid="wa-expiry" className={inputCls} value={form.contract_expiry} onChange={set("contract_expiry")} placeholder="e.g. 03/2027 or Unknown" /></Field>
        </div>
      )}

      {step === 3 && (
        <div className="mt-6 space-y-6">
          <Field label="Anything else about your waste stream?">
            <textarea data-testid="wa-message" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Volumes, sites, current provider, objectives…" />
          </Field>
          <div>
            <label className={labelCls}>Upload recent waste invoice & material photographs (optional)</label>
            <label className="flex items-center gap-3 border border-dashed border-dc-borderHi p-6 cursor-pointer hover:border-dc-accent transition-colors">
              <Upload size={20} className="text-dc-accent" />
              <span className="text-sm text-dc-text2">{files.length ? `${files.length} file(s) selected` : "Drop or select files (PDF, JPG, PNG)"}</span>
              <input data-testid="wa-files" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} />
            </label>
          </div>
          <p className="text-[11px] text-dc-muted leading-relaxed">
            No obligation. Commercial viability depends on material quality, volume, location, logistics and prevailing market conditions.
          </p>
        </div>
      )}

      <div className="flex justify-between items-center mt-10">
        {step > 1 ? (
          <button data-testid="wa-back" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-2 text-dc-text2 hover:text-white transition-colors text-sm"><ArrowLeft size={16} /> Back</button>
        ) : <span />}
        {step < 3 ? (
          <button
            data-testid="wa-next"
            onClick={() => { if (step === 1 && !step1Valid) { toast.error("Please complete all fields"); return; } setStep((s) => s + 1); }}
            className="inline-flex items-center gap-2 bg-dc-accent text-dc-bg font-semibold px-6 py-3 hover:bg-dc-accentHover transition-colors text-sm"
          >
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button data-testid="wa-submit" disabled={submitting} onClick={submit} className="inline-flex items-center gap-2 bg-dc-accent text-dc-bg font-semibold px-6 py-3 hover:bg-dc-accentHover transition-colors text-sm disabled:opacity-60">
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Assess My Waste Stream <ArrowRight size={16} /></>}
          </button>
        )}
      </div>
    </div>
  );
};
